import React, { useMemo } from 'react';
import { QMenuButton, QMenuItemType } from '@qualio/ui-components';
import { useEventPermissions } from '../../../../hooks';
import { CancelEvent, CloseEarly, OpenEvent } from '../../../../displayStrings';
import i18n from '../../../../i18n/config';

type menuProps = {
  eventStatus: string;
  eventId: number;
  onEditEvent: (eventId: number) => void;
  onChangeOwner: (eventId: number) => void;
  onEscalateEvent: (eventId: number) => void;
  onCloseEvent: (eventId: number) => void;
  onCancelEvent: (eventId: number) => void;
  onReopen: (eventId: number) => void;
  onOpen: (eventId: number) => void;
  onUpdateEventProperties: (eventId: number) => void;
};

const menuItems = {
  edit: {
    id: 'edit',
    label: i18n.t('common.actions.edit'),
  },
  changeOwner: {
    id: 'changeOwner',
    label: i18n.t('events.actions.changeOwner'),
  },
  escalateEvent: {
    id: 'escalateEvent',
    label: i18n.t('events.actions.escalateAndCreateNewEvent'),
  },
  close: {
    id: 'close',
    label: CloseEarly,
  },
  cancel: {
    id: 'cancel',
    label: CancelEvent,
  },
  updateEventProperties: {
    id: 'updateEventProperties',
    label: i18n.t('events.actions.updateEventProperties'),
  },
  reopen: {
    id: 'reopen',
    label: i18n.t('common.labels.reopen'),
  },
  open: {
    id: 'open',
    label: OpenEvent,
  },
};

const KebabMenu: React.FC<menuProps> = ({
  eventStatus,
  eventId,
  onEditEvent,
  onChangeOwner,
  onEscalateEvent,
  onCloseEvent,
  onCancelEvent,
  onReopen,
  onOpen,
  onUpdateEventProperties,
}) => {
  const { canModifyEvent, canChangeOwner, canReopenEvent, canCloseEventEarly, canOpenImportedEvent } =
    useEventPermissions();
  const handleMenuClick = (item: QMenuItemType) => {
    if (item.id === 'edit') {
      onEditEvent(eventId);
    }
    if (item.id === 'changeOwner') {
      onChangeOwner(eventId);
    }
    if (item.id === 'escalateEvent') {
      onEscalateEvent(eventId);
    }
    if (item.id === 'close') {
      onCloseEvent(eventId);
    }
    if (item.id === 'cancel') {
      onCancelEvent(eventId);
    }
    if (item.id === 'reopen') {
      onReopen(eventId);
    }
    if (item.id === 'open') {
      onOpen(eventId);
    }
    if (item.id === 'updateEventProperties') {
      onUpdateEventProperties(eventId);
    }
  };

  const renderMenuItems = useMemo(() => {
    const isStatus = (expected: string[]) => expected.includes(eventStatus);
    const items = [];
    if (canChangeOwner) {
      items.push(menuItems.changeOwner);
    }
    if (canOpenImportedEvent && isStatus(['imported'])) {
      items.push(menuItems.open);
    }
    if (canModifyEvent) {
      items.push(menuItems.escalateEvent);
    }
    if (canCloseEventEarly && isStatus(['imported', 'open'])) {
      items.push(menuItems.close);
    }
    if (canModifyEvent && isStatus(['open'])) {
      items.push(menuItems.cancel);
    }
    if (canReopenEvent && isStatus(['closed', 'rejected', 'cancelled'])) {
      items.push(menuItems.reopen);
    }
    if (canModifyEvent && isStatus(['closed'])) {
      items.push(menuItems.updateEventProperties);
    }
    return items;
  }, [canChangeOwner, canOpenImportedEvent, canModifyEvent, canCloseEventEarly, canReopenEvent, eventStatus]);

  return (
    <>
      {renderMenuItems.length > 0 && (
        <QMenuButton
          buttonLabel="Options"
          iconName="MoreVertical"
          itemSize="sm"
          items={renderMenuItems}
          onItemClick={handleMenuClick}
          variant="icon"
        />
      )}
    </>
  );
};

export default KebabMenu;
